import { useEffect, useState } from 'react';
import { apiFetchBlob } from '../../lib/api.js';
import { t } from '../../lib/i18n.js';

/**
 * An image held in storage, fetched with the user's token.
 *
 * ADR-0013: the frontend never receives a raw storage URL, so a stored photo
 * cannot simply be an `<img src>` pointing at S3. It also cannot point at the
 * API directly — an image element sends no Authorization header, and the file
 * route authenticates like every other route (ADR-0015). So the bytes are
 * fetched here and turned into an object URL.
 *
 * The object URL is revoked on unmount. A log with thirty photos that never
 * released them would hold thirty blobs alive for as long as the tab is open.
 */
export function StoredImage({
  fileId,
  alt,
  className,
}: {
  fileId: string;
  alt: string;
  className?: string;
}) {
  const [src, setSrc] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let revoked = false;
    let url: string | null = null;

    apiFetchBlob(`/files/${fileId}`)
      .then((blob) => {
        if (revoked) return;
        url = URL.createObjectURL(blob);
        setSrc(url);
      })
      .catch(() => setFailed(true));

    return () => {
      revoked = true;
      if (url) URL.revokeObjectURL(url);
    };
  }, [fileId]);

  if (failed) {
    return (
      <div
        className={`${className ?? ''} flex items-center justify-center bg-surface-sunken text-xs text-content-secondary`}
      >
        {t("Photo unavailable")}
      </div>
    );
  }

  if (!src) {
    return <div className={`${className ?? ''} animate-pulse bg-surface-sunken`} aria-hidden />;
  }

  return <img src={src} alt={alt} className={className} />;
}
